import { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";

const emptyForm = {
  titulo: "",
  descripcion: "",
  bulletsText: "", // textarea (1 bullet por línea)
  ctaText: "Agendar sesión",
  ctaLink: "/agenda",
  orden: 1,
  activo: true,
  imagenFile: null,
  imagenURL: "",
};

function bulletsTextToArray(text) {
  return (text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function bulletsArrayToText(arr) {
  if (!Array.isArray(arr)) return "";
  return arr.filter(Boolean).join("\n");
}

export default function ServiciosDestacadosEditor() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  // =========================
  // LISTENER
  // =========================
  useEffect(() => {
    const q = query(
      collection(db, "servicios_destacados"),
      orderBy("orden", "asc")
    );

    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  // =========================
  // HANDLERS
  // =========================
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "file"
          ? files[0]
          : value,
    }));
  };

  const subirImagen = async (file) => {
    const safeName = (file.name || "img").replace(/\s+/g, "_");
    const imgRef = ref(storage, `servicios_destacados/${Date.now()}-${safeName}`);
    await uploadBytes(imgRef, file);
    return await getDownloadURL(imgRef);
  };

  const guardar = async (e) => {
    e.preventDefault();

    let imagenURL = form.imagenURL;

    if (form.imagenFile) {
      imagenURL = await subirImagen(form.imagenFile);
    }

    const data = {
      titulo: form.titulo,
      descripcion: form.descripcion,
      bullets: bulletsTextToArray(form.bulletsText),
      ctaText: form.ctaText || "Agendar sesión",
      ctaLink: form.ctaLink || "/agenda",
      orden: Number(form.orden) || 1,
      activo: form.activo,
      imagenURL,
      updatedAt: serverTimestamp(),
    };

    if (editingId) {
      await updateDoc(doc(db, "servicios_destacados", editingId), data);
    } else {
      await addDoc(collection(db, "servicios_destacados"), {
        ...data,
        createdAt: serverTimestamp(),
      });
    }

    setForm(emptyForm);
    setEditingId(null);
  };

  const editar = (d) => {
    setForm({
      titulo: d.titulo || "",
      descripcion: d.descripcion || "",
      bulletsText: bulletsArrayToText(d.bullets),
      ctaText: d.ctaText || "Agendar sesión",
      ctaLink: d.ctaLink || "/agenda",
      orden: d.orden || 1,
      activo: d.activo ?? true,
      imagenURL: d.imagenURL || "",
      imagenFile: null,
    });

    setEditingId(d.id);
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar servicio destacado?")) return;
    await deleteDoc(doc(db, "servicios_destacados", id));
  };

  if (loading) return <p>Cargando servicios destacados…</p>;

  return (
    <div className="space-y-6">
      {/* ========================= FORMULARIO ========================= */}
      <form
        onSubmit={guardar}
        className="bg-gray-50 p-6 rounded-xl space-y-4 border"
      >
        <h3 className="text-lg font-bold text-blue-900">
          {editingId ? "Editar servicio destacado" : "Agregar servicio destacado"}
        </h3>

        {/* TITULO */}
        <label className="block">
          <span className="font-semibold text-sm">Título</span>
          <input
            name="titulo"
            value={form.titulo}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
            required
          />
        </label>

        {/* DESCRIPCION */}
        <label className="block">
          <span className="font-semibold text-sm">Descripción</span>
          <textarea
            name="descripcion"
            value={form.descripcion}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
            rows={3}
          />
        </label>

        {/* BULLETS */}
        <label className="block">
          <span className="font-semibold text-sm">
            Bullets (1 por línea)
          </span>
          <textarea
            name="bulletsText"
            value={form.bulletsText}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
            rows={4}
            placeholder={"Ej:\nEvaluación inicial y objetivos claros.\nRutinas progresivas y seguimiento constante."}
          />
        </label>

        {/* CTA */}
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="font-semibold text-sm">Texto botón (CTA)</span>
            <input
              name="ctaText"
              value={form.ctaText}
              onChange={handleChange}
              className="w-full border rounded-lg p-2 mt-1"
            />
          </label>

          <label className="block">
            <span className="font-semibold text-sm">Link botón (CTA)</span>
            <input
              name="ctaLink"
              value={form.ctaLink}
              onChange={handleChange}
              className="w-full border rounded-lg p-2 mt-1"
              placeholder="/agenda"
            />
          </label>
        </div>

        {/* ORDEN */}
        <label className="block">
          <span className="font-semibold text-sm">Orden (posición en la web)</span>
          <input
            type="number"
            name="orden"
            value={form.orden}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 mt-1"
          />
        </label>

        {/* IMAGEN ACTUAL */}
        {form.imagenURL && (
          <div>
            <p className="font-semibold text-sm mb-1">Imagen actual</p>
            <img
              src={form.imagenURL}
              alt="Imagen actual"
              className="w-40 h-28 object-cover rounded-lg border shadow"
            />
          </div>
        )}

        {/* SUBIR / CAMBIAR IMAGEN */}
        <label className="block">
          <span className="font-semibold text-sm">
            {form.imagenURL ? "Cambiar imagen" : "Subir imagen"}
          </span>
          <input
            type="file"
            accept="image/*"
            name="imagenFile"
            onChange={handleChange}
            className="mt-1"
          />
        </label>

        {/* ACTIVO */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="activo"
            checked={form.activo}
            onChange={handleChange}
          />
          <span className="text-sm font-medium">Visible en la web</span>
        </label>

        {/* BOTONES */}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            {editingId ? "Guardar cambios" : "Agregar servicio"}
          </button>

          {editingId && (
            <button
              type="button"
              onClick={() => {
                setForm(emptyForm);
                setEditingId(null);
              }}
              className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400"
            >
              Cancelar
            </button>
          )}
        </div>
      </form>

      {/* ========================= LISTA ========================= */}
      <div className="space-y-3">
        {items.map((d) => (
          <div
            key={d.id}
            className="border rounded-xl p-3 sm:p-4 bg-white"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              {/* BLOQUE IZQUIERDO */}
              <div className="flex items-start gap-3 min-w-0">
                <img
                  src={d.imagenURL}
                  alt={d.titulo}
                  className="w-20 h-20 sm:w-24 sm:h-16 object-cover rounded-lg border shrink-0"
                />

                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-base sm:text-lg break-words">
                    {d.titulo}
                  </p>

                  <p className="text-sm text-gray-500 line-clamp-3 break-words">
                    {d.descripcion}
                  </p>

                  <p className="text-xs text-gray-400 mt-1 break-words">
                    CTA: {d.ctaText || "Agendar sesión"} → {d.ctaLink || "/agenda"}
                  </p>
                </div>
              </div>

              {/* BLOQUE DERECHO */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:justify-end">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    d.activo
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {d.activo ? "Activo" : "Inactivo"}
                </span>

                <button
                  onClick={() => editar(d)}
                  className="text-blue-600 hover:underline text-sm whitespace-nowrap"
                >
                  Editar
                </button>

                <button
                  onClick={() => eliminar(d.id)}
                  className="text-red-600 hover:underline text-sm whitespace-nowrap"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}

        {!items.length && (
          <p className="text-sm text-gray-500">
            Aún no hay servicios destacados creados.
          </p>
        )}
      </div>
    </div>
  );
}
